import { Layer, Text, Stage, Image, Rect } from "konva";
import QRCode from 'qrcode';
import { getIconComponent } from "../Utilities/IconUtils";
import ReactDOMServer from "react-dom/server";
import { transformText } from ".";
// 1. Check if fonts are loaded via CSS first
const checkCSSFonts = () => {
  const testDiv = document.createElement('div');
  testDiv.style.fontFamily = 'FuturaBold';
  testDiv.style.fontSize = '12px';
  testDiv.textContent = 'Test';
  testDiv.style.position = 'absolute';
  testDiv.style.left = '-9999px';
  document.body.appendChild(testDiv);
  
  const computedStyle = window.getComputedStyle(testDiv);
  const actualFont = computedStyle.fontFamily;
  
  document.body.removeChild(testDiv);
  
  return actualFont.includes('FuturaBold');
};

// 2. Enhanced Font Loading with Preload
const preloadFonts = async () => {
  const fontUrls = [
    './assets/fonts/futura-bold.ttf',
    './assets/fonts/arial-bold.ttf', 
    './assets/fonts/metropolis-bold.ttf'
  ];
  
  // Preload font files
  const preloadPromises = fontUrls.map(url => {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = url;
      link.as = 'font';
      link.type = 'font/ttf';
      link.crossOrigin = 'anonymous';
      
      link.onload = () => {
        console.log(`Font preloaded: ${url}`);
        resolve(url);
      };
      link.onerror = () => {
        reject(new Error(`Font not found: ${url}`));
      };
      
      document.head.appendChild(link);
    });
  });
  
  try {
    await Promise.all(preloadPromises);
  } catch (error) {
  }
};

// 3. Font Loading with FontFace API + Fallback
const loadFontsWithFallback = async () => {
  const fontConfigs = [
    { family: 'FuturaBold', url: './assets/fonts/futura-bold.ttf' },
    { family: 'Arial', url: './assets/fonts/arial-bold.ttf' },
    { family: 'Metropolis', url: './assets/fonts/metropolis-bold.ttf' }
  ];
  
  const loadResults = {};
  
  for (const config of fontConfigs) {
    try {
      // Check if font file exists first
      const response = await fetch(config.url, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error(`Font file not found: ${config.url}`);
      }
      
      // Load font using FontFace API
      const font = new FontFace(config.family, `url(${config.url})`);
      const loadedFont = await font.load();
      document.fonts.add(loadedFont);
      
      loadResults[config.family] = true;
    } catch (error) {
      loadResults[config.family] = false;
    }
  }
  
  return loadResults;
};

// 4. Comprehensive Font Detection
const detectFont = (fontName) => {
  // Method 1: Canvas text measurement
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  
  const testText = 'mmmmmmmmmmlli';
  const testSize = 72;
  
  // Measure with serif fallback
  context.font = `${testSize}px serif`;
  const serifWidth = context.measureText(testText).width;
  
  // Measure with sans-serif fallback
  context.font = `${testSize}px sans-serif`;
  const sansWidth = context.measureText(testText).width;
  
  // Measure with target font
  context.font = `${testSize}px ${fontName}, serif`;
  const targetSerifWidth = context.measureText(testText).width;
  
  context.font = `${testSize}px ${fontName}, sans-serif`;
  const targetSansWidth = context.measureText(testText).width;
  
  const isAvailable = (targetSerifWidth !== serifWidth) || (targetSansWidth !== sansWidth);
  
  // Method 2: document.fonts.check (if available)
  let fontsApiCheck = false;
  if (document.fonts && document.fonts.check) {
    fontsApiCheck = document.fonts.check(`${testSize}px ${fontName}`);
  }
  
 
  
  return isAvailable || fontsApiCheck;
};

// 5. Complete Font Initialization
export const initializeFontsComplete = async () => {
  
  // Step 1: Check CSS fonts
  const cssLoaded = checkCSSFonts();
  
  // Step 2: Preload fonts
  await preloadFonts();
  
  // Step 3: Load with FontFace API
  const loadResults = await loadFontsWithFallback();
  
  // Step 4: Wait for fonts to be ready
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready;
  }
  
  // Step 5: Test all fonts
  const testFonts = ['FuturaBold', 'Arial', 'Metropolis'];
  const fontStatus = {};
  
  testFonts.forEach(font => {
    const isAvailable = detectFont(font);
    fontStatus[font] = isAvailable;
    console.log(`${isAvailable ? '✅' : '❌'} ${font}: ${isAvailable ? 'Available' : 'Not Available'}`);
  });
  
  return { loadResults, fontStatus };
};
export const imageConverter = async ({ elements, filterElements, canvasSize, backgroundUrl }) => {
  console.log("backgroundUrl",backgroundUrl)
  return new Promise(async (res, rej) => {
    try {
      let width = canvasSize?.width 
      let height = canvasSize?.height 
      const strokeW = 1;
      const padding = 2;
      let canvasNeedsResize = false;
      let maxRequiredWidth = width;
      let maxRequiredHeight = height;
      let lineHeightFont = 1;
      let bgNode;
      
      const createStage = (w, h) => {
        const stage = new Stage({
          width: w,
          height: h,
          container: document.createElement("div"),
        });
        
        const layer = new Layer();
        stage.add(layer);
        
        // White background
        const background = new Rect({
          x: 0,
          y: 0,
          width: w,
          height: h,
          fill: "white",
        });
        layer.add(background);
        
        return { stage, layer };
      };
      
      const { stage: printStage, layer: printLayer } = createStage(width, height);

      // Load and draw background image if provided
      if (backgroundUrl) {
        await new Promise((resolve) => {
          const bgImg = new window.Image();
          bgImg.crossOrigin = "Anonymous";
          bgImg.onload = () => {
            bgNode = new Image({
              x: 0,
              y: 0,
              image: bgImg,
              width: width,
              height: height,
            });
            printLayer.add(bgNode);
            resolve();
          };
          bgImg.onerror = () => {
            console.error("Failed to load background image:", backgroundUrl);
            resolve(); // Still resolve to continue with elements
          };
          bgImg.src = backgroundUrl;
        });
      }
      
      // Sort elements by priority: QR codes come first, then other elements
      const sortedElements = [...elements].sort((a, b) => {
        if (a.type === "qrcode") return -1;
        if (b.type === "qrcode") return 1;
        return 0;
      });
      
      // Keep track of occupied spaces for collision detection
      const occupiedSpaces = [];
      
      // First pass - process QR codes first to establish their positions
      for (const el of sortedElements.filter(e => e.type === "qrcode"||e.type=="icon")) {
        el.content="";
        if(el.type=="qrcode"){
          // Generate QR code
        const qrDataUrl = await QRCode.toDataURL(
          JSON.stringify({
            id: filterElements?.ticketId || filterElements?.orderId,
            source: 1
          }),
          {
            width: el.width,
            height: el.height,
            margin: 0,
          }
        );
        
        // Mark QR code space as occupied with highest priority
        occupiedSpaces.push({
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
          type: 'qr',
          priority: 10,
          el
        });
        
        // Create and wait for QR image to load
        await new Promise((resolve) => {
          const qrImage = new window.Image();
          qrImage.onload = () => {
            const qrNode = new Image({
              x: el.x,
              y: el.y,
              image: qrImage,
              width: el.width,
              height: el.height,
            });
            printLayer.add(qrNode);
            resolve();
          };
          qrImage.src = qrDataUrl;
        });
        }else if(el.type=="icon"){
          const Icon=getIconComponent(el.key);
          if (!Icon) {
           console.warn(`Unknown icon: ${el.key}`);
           continue;
          }
          const svgMarkup = ReactDOMServer.renderToStaticMarkup(
            <Icon color={el.color || "#000"} size={el.width} />
          );
        const blob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        
        // Mark QR code space as occupied with highest priority
        occupiedSpaces.push({
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
          type: 'qr',
          priority: 10,
          el
        });
        
        // Create and wait for QR image to load
        await new Promise((resolve) => {
           const iconImage = new window.Image();
          iconImage.onload = () => {
            const qrNode = new Image({
              x: el.x,
              y: el.y,
              image: iconImage,
              width: el.width,
              height: el.height,
            });
            printLayer.add(qrNode);
            resolve();
          };
          iconImage.src = url;
        });
        }
      }
      
      // Second pass - analyze text elements to plan layouts
      const textElements = sortedElements.filter(el => el.type !== "qrcode"&&el.type !== "icon");
      
      // Group text elements by vertical position to better handle elements in the same row
      const textRowGroups = {};
      textElements.forEach(el => {
        // Group by y position rounded to nearest 10px to handle slight differences
        const rowKey = Math.round(el.y / 10) * 10;
        if (!textRowGroups[rowKey]) textRowGroups[rowKey] = [];
        textRowGroups[rowKey].push(el);
      });
      
      // Sort row groups by y position
      const sortedRows = Object.keys(textRowGroups).sort((a, b) => parseInt(a) - parseInt(b));
      
      // Process each row
      for (const rowKey of sortedRows) {
        const rowElements = textRowGroups[rowKey];
        
        // Sort elements in each row by x position
        rowElements.sort((a, b) => a.x - b.x);
        
        // Calculate available width for each element
        rowElements.forEach((el, index) => {
          const nextElement = rowElements[index + 1];
          let rightBoundary = width - padding;
          
          // If there's a next element in the row, that's our right boundary
          if (nextElement) {
            rightBoundary = nextElement.x;
          } else {
            // Otherwise, check for any element that might be to the right
            occupiedSpaces.forEach(space => {
              // If there's an occupied space to the right of this element
              if (space.x > el.x && space.y < el.y + el.height && space.y + space.height > el.y) {
                rightBoundary = Math.min(rightBoundary, space.x);
              }
            });
          }
          
          // Calculate max available width for this element
          el.availableWidth = rightBoundary - el.x - padding;
        });
      }
      
      // Process rows for Universal Merging (Green Tick Logic)
      for (const rowKey of sortedRows) {
        const rowElements = textRowGroups[rowKey];
        if (rowElements.length === 0) continue;

        // Sort by X to merge in order
        rowElements.sort((a, b) => a.x - b.x);

        // MERGE LOGIC: Combine all elements in the row
        const firstEl = rowElements[0];
        let combinedContent = "";
        
        for (let i = 0; i < rowElements.length; i++) {
          const el = rowElements[i];
          let elContent = el?.isLocation ? el?.content : filterElements[el.key?.toLowerCase()] || el.content;
          elContent = transformText(elContent, el?.textTransformation);
          
          if (elContent && elContent.trim() !== "") {
            combinedContent += (combinedContent ? " " : "") + elContent;
          }
          // Mark as handled
          el._handled = true;
        }

        if (!combinedContent) continue;

        // Render the combined element as a single unit
        const el = firstEl;
        let finalX = el.x;
        let finalY = el.y;
        let fontSize = el.fontSize;
        
        // Use a large width for merged lines to prevent wrapping
        let finalWidth = 1000; 
        let finalHeight = el.height * 2;

        const textNode = new Text({
          x: el.textAlign === "center" ? 0 : finalX,
          y: finalY,
          text: combinedContent,
          fontSize: fontSize,
          fontFamily: el.fontFamily,
          fill: el.color || "black",
          fontStyle: el.fontStyle,
          fontWeight: el.fontWeight,
          textDecoration: el.textDecoration,
          width: el.textAlign === "center" ? width : finalWidth,
          height: finalHeight,
          align: el.textAlign || "left",
          wrap: "none"
        });
        
        printLayer.add(textNode);
      }
      
      // Visible dashed border on all sides (commented out)
      const border = new Rect({
        x: 0.1,
        y: 0.1,
        width: maxRequiredWidth - strokeW,
        height: Math.round(maxRequiredHeight) - strokeW,
        stroke: "black",
        strokeWidth: 1,
        // dash: [6, 6],
      });
      // printLayer.add(border);
      
      // Draw the layer and export
      printLayer.draw();
      
      // Convert to high-quality data URL
      const dataUrl = printStage.toDataURL({
        pixelRatio: 1,
        mimeType: "image/png",
        quality: 1,
      });
      
      if (bgNode) {
          bgNode.visible(false);
          printStage.draw();
      }
      const rawUrl = printStage.toDataURL({ pixelRatio: 1});
      
      printStage.destroy();
      res({
        dataUrl,
        height: Math.round(maxRequiredHeight),
        width: maxRequiredWidth,
        rawUrl
      });
    } catch (error) {
      rej(error);
    }
  });
};